import React, { useState, useEffect } from 'react';
import { CanopiLogo, CanopiWordmark } from './CanopiLogo';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

export default function Navigation() {
  const { lang, toggleLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { label: t('nav.problem'),  href: '/#problem' },
    { label: t('nav.solution'), href: '/#solution' },
    { label: t('nav.platform'), href: '/#platform' },
    { label: t('nav.roadmap'),  href: '/#roadmap' },
    { label: t('nav.about'),    href: '/#about' },
    { label: t('nav.research'), href: '/research' },
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed z-50 transition-all duration-500 ease-in-out ${
        scrolled || mobileOpen
          ? 'top-2 left-3 right-3 rounded-2xl bg-[#0C0F0C]/92 backdrop-blur-2xl border border-[#3D5638]/25 shadow-[0_4px_24px_rgba(0,0,0,0.4)]'
          : 'top-4 left-4 right-4 rounded-2xl bg-transparent border border-transparent'
      }`}
    >
      <div
        className={`flex items-center justify-between transition-all duration-500 ${
          scrolled ? 'px-5 py-2' : 'px-6 py-3.5'
        }`}
      >
        {/* Logo */}
        <a href="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <CanopiLogo
            size={scrolled ? 26 : 32}
            className="text-white/90 group-hover:text-white transition-all duration-500"
          />
          <CanopiWordmark
            className={`inline transition-all duration-500 ${scrolled ? 'text-xs' : 'text-sm'}`}
          />
        </a>

        {/* Desktop links */}
        <div className="hidden lg:flex items-center gap-7">
          {navLinks.map(link => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.href.replace('#', '')}`}
              className={`text-white/55 hover:text-white transition-all duration-300 font-medium tracking-wide ${
                scrolled ? 'text-xs' : 'text-sm'
              }`}
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Desktop CTA + language toggle */}
        <div className="hidden lg:flex items-center gap-3">
          {/* EN / ES pill toggle */}
          <button
            onClick={toggleLang}
            data-testid="nav-lang-toggle"
            aria-label="Toggle language"
            className={`font-mono tracking-widest transition-all duration-300 border rounded-full ${
              scrolled ? 'text-[10px] px-3 py-1' : 'text-xs px-3.5 py-1.5'
            } ${
              lang === 'es'
                ? 'border-[#BDD6B5]/50 text-[#BDD6B5] bg-[#3D5638]/20'
                : 'border-white/15 text-white/45 hover:text-white/70 hover:border-white/30'
            }`}
          >
            {lang === 'en' ? 'ES' : 'EN'}
          </button>

          <a
            href="#interest"
            data-testid="nav-waitlist-button"
            className={`text-white/70 hover:text-white transition-colors font-medium ${
              scrolled ? 'text-xs' : 'text-sm'
            }`}
          >
            {t('nav.waitlist')}
          </a>
          <a
            href="/login"
            data-testid="nav-login-button"
            className={`bg-[#3D5638] hover:bg-[#4A6845] text-white rounded-full font-medium transition-all duration-300 shadow-[0_0_12px_rgba(61,86,56,0.25)] hover:shadow-[0_0_22px_rgba(61,86,56,0.4)] ${
              scrolled ? 'text-xs px-4 py-1.5' : 'text-sm px-6 py-2'
            }`}
          >
            {t('nav.login')}
          </a>
        </div>

        {/* Mobile: lang toggle + hamburger */}
        <div className="flex lg:hidden items-center gap-2">
          <button
            onClick={toggleLang}
            aria-label="Toggle language"
            className="font-mono text-[10px] tracking-widest border border-white/15 text-white/45 hover:text-white/70 rounded-full px-2.5 py-1 transition-colors"
          >
            {lang === 'en' ? 'ES' : 'EN'}
          </button>
          <button
            data-testid="nav-mobile-menu"
            className="text-white/70 p-1"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden px-5 pb-4 border-t border-[#3D5638]/25">
          <div className="flex flex-col gap-2 pt-3">
            {navLinks.map(link => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-mobile-link-${link.href.replace('#', '')}`}
                className="text-sm text-white/60 hover:text-white py-2 font-medium"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <div className="flex gap-3 pt-2 border-t border-[#3D5638]/25">
              <a href="#interest" className="text-sm text-white/70 font-medium py-2">{t('nav.waitlist')}</a>
              <a href="/login" className="text-sm bg-[#3D5638] text-white rounded-full px-5 py-2 font-medium">{t('nav.login')}</a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
